import {advanceDiagnosticConfig} from '../../config/advanceDiagnosticConfig/advanceDiagnosticConfig';
import advanceDiagnosticLocalizationConfig from '../../config/advanceDiagnosticConfig/advanceDiagnosticLocalizationConfig';
import advanceDiagnosticPartialConfig from '../../config/advanceDiagnosticConfig/advanceDiagnosticPartialConfig';
import * as advanceDiagnosticScreenSteps from '../../config/advanceDiagnosticConfig/advanceDiagnosticScreenSteps';
import appConfig from '../../config/appConfig';
import * as storageKeys from '../../consts/app/storageKeys';
import {ENABLE_ERRORS_DISPLAY} from '../../consts/tnc/aggregatedSettingTypes';
import advanceDiagnosticFailedErrorCodes from '../../consts/uam/advanceDiagnosticFailedErrorCodes';
import * as dataCollectionTypes from '../../consts/uam/dataCollectionTypes';
import * as advanceDiagnosticScreenTypes from '../../consts/yap/advanceDiagnosticScreenTypes';
import log from '../../services/logger/log';
import {
    clearAdvancedDiagnosticState,
    setDiagnosticFlowDataResult,
    setFailedAttempts,
} from '../../state/ducks/advancedDiagnostic';
import {
    selectDiagnosticFlowDataResult,
    selectFailedAttempts,
    selectIsDiagnosticFlowDataCollectionInProgress,
} from '../../state/selectors/advancedDiagnostic';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../../state/selectors/consumer';
import {
    makeSelectSerialNumber,
    selectIotDeviceData,
    selectIsHolderConnected,
    selectIsMonoDevice,
    selectProductName,
} from '../../state/selectors/iotDevice';
import {dispatch, getState} from '../../state/store';
import {amplitudeADTracking} from '../amplitude';
import analyticsService from '../analyticsService';
import appErrorService from '../app/appErrorService';
import fwuPackageService from '../fwu/fwuPackageService';
import iotDeviceSettingsService from '../iotDevice/iotDeviceSettingsService';
import {getLocalizedStrings} from '../localization/localizationService';
import marketConfigService from '../marketConfigService';
import pairingService from '../pairingService';
import appRouterService from '../route/appRouterService';
import routerService from '../routerService';
import storageService from '../storage/storageService';
import tncService from '../tncService';
import * as dataCollectionService from '../uam/dataCollectionService';

const checkDiagnosticResult = (isHolder) => {
    const state = getState();
    const curDiagnosticResult = selectDiagnosticFlowDataResult(state) || {};
    const isFinished = isDiagnosticFinished(curDiagnosticResult);

    if (isFinished) return;

    const {
        isDeviceInferenceCompleted,
        isHolderInferenceCompleted,
        isDeviceReceived,
        isHolderReceived,
        deviceInferenceCode,
        holderInferenceCode,
        holderDeviceState,
        deviceState: chargerDeviceState,
    } = curDiagnosticResult;
    const inferenceCode = isHolder ? holderInferenceCode : deviceInferenceCode;
    const deviceState = isHolder ? holderDeviceState : chargerDeviceState;
    const isReceived = isHolder ? isHolderReceived : isDeviceReceived;
    const isInferenceCompleted = isHolder ? isHolderInferenceCompleted : isDeviceInferenceCompleted;

    //if inferenceCode or deviceState already exists and dataCollection is ended - end diagnostic flow
    if ((inferenceCode || deviceState) && isReceived) {
        setDiagnosticSuccess(isHolder);
        return;
    }

    //wait for INFERENCE_COMPLETED event
    if (inferenceCode === undefined && !isInferenceCompleted) return;

    if (!isReceived) {
        const isDiagnosticFlowDataCollectionInProgress = selectIsDiagnosticFlowDataCollectionInProgress(state);

        //wait for dataCollection event is ended from uam
        if (isDiagnosticFlowDataCollectionInProgress) return;

        //something goes wrong. if isDiagnosticFlowDataCollectionInProgress is not in progress then isReceived should be true
        setDiagnosticFailed(isHolder);
        return;
    }

    //if request for inferenceCode is failed or inferenceName is incorrect - end diagnostic flow
    if (inferenceCode === undefined && deviceState === undefined) {
        setDiagnosticFailed(isHolder);
        return;
    }

    //if dataCollection ends for holder - end diagnostic flow
    if (isHolder) {
        setDiagnosticSuccess(isHolder);
        return;
    }

    //if dataCollection ends for charger - continue diagnostic flow for holder
    setDiagnosticSuccess(isHolder);
};

const isDiagnosticSuccess = (diagnosticResult) => {
    const isNeedDiagnosticHolder = getIsNeedDiagnosticHolder();
    const {isDeviceSuccess, isHolderSuccess} = diagnosticResult || {};

    return isDeviceSuccess === true && (!isNeedDiagnosticHolder || isHolderSuccess === true);
};

const isDiagnosticFinished = (diagnosticResult) => {
    const isNeedDiagnosticHolder = getIsNeedDiagnosticHolder();
    const {isDeviceSuccess, isHolderSuccess} = diagnosticResult || {};

    //Undefined means inProgress
    return isDeviceSuccess !== undefined && (!isNeedDiagnosticHolder || isHolderSuccess !== undefined);
};

const setDiagnosticSuccess = (isHolder) => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const isNeedDiagnosticHolder = getIsNeedDiagnosticHolder();

    const isSuccess = isHolder ? {isHolderSuccess: true} : {isDeviceSuccess: true};

    dispatch(setDiagnosticFlowDataResult({...curDiagnosticResult, ...isSuccess, isHolder}));

    if (!isNeedDiagnosticHolder || isHolder) {
        dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC);
    }
};

const setInferenceCompleted = (isHolder) => {
    const curDiagnosticResult = getDiagnosticReceivedData();

    const isInferenceCompleted = isHolder ? {isHolderInferenceCompleted: true} : {isDeviceInferenceCompleted: true};

    dispatch(setDiagnosticFlowDataResult({...curDiagnosticResult, ...isInferenceCompleted}));
};

const setDiagnosticFailed = (isHolder) => {
    const curDiagnosticResult = getDiagnosticReceivedData();

    const isSuccess = isHolder ? {isHolderSuccess: false} : {isDeviceSuccess: false};

    increaseFailedAttempt(isHolder);
    dispatch(setDiagnosticFlowDataResult({...curDiagnosticResult, ...isSuccess}));

    dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC);
};

const setDiagnosticPartResult = ({deviceState, inferenceCode, inferenceCode_L1, isHolder}) => {
    const curDiagnosticResult = getDiagnosticReceivedData();
    const diagnosticResultObj = isHolder
        ? {
              ...curDiagnosticResult,
              holderInferenceCode: inferenceCode,
              holderInferenceCode_L1: inferenceCode_L1,
              holderDeviceState: deviceState,
          }
        : {
              ...curDiagnosticResult,
              deviceInferenceCode: inferenceCode,
              deviceInferenceCode_L1: inferenceCode_L1,
              deviceState,
          };

    dispatch(setDiagnosticFlowDataResult(diagnosticResultObj));
};

const getDiagnosticReceivedData = () => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};

    return {
        isHolderReceived: curDiagnosticResult?.isHolderReceived,
        isDeviceReceived: curDiagnosticResult?.isDeviceReceived,
        ...curDiagnosticResult,
    };
};

const setDiagnosticReceived = ({isHolder = false, isReceived = false}) => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};

    const diagnosticResultObj = isHolder
        ? {...curDiagnosticResult, isHolderReceived: isReceived}
        : {...curDiagnosticResult, isDeviceReceived: isReceived};

    dispatch(setDiagnosticFlowDataResult(diagnosticResultObj));
};

const getDeviceAdConfig = (isHolder) => {
    const state = getState();
    const {device, holder} = selectIotDeviceData(state) || {};
    const {type} = (isHolder ? holder : device) || {};
    const deviceAdConfig = advanceDiagnosticConfig.find((x) => x.type === type)?.config;

    return deviceAdConfig;
};

const getDeviceAdDefaultFlow = (isHolder) => {
    const state = getState();
    const {device, holder} = selectIotDeviceData(state) || {};
    const {type} = (isHolder ? holder : device) || {};
    const defaultFlow = advanceDiagnosticConfig.find((x) => x.type === type)?.defaultFlow;

    return defaultFlow;
};

const isSpecialFlow = (inferenceCode, isHolder) => {
    const deviceAdConfig = getDeviceAdConfig(isHolder);
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const {deviceState: chargerDeviceState, holderDeviceState} = curDiagnosticResult;
    const deviceState = isHolder ? holderDeviceState : chargerDeviceState;

    const adConfig = deviceAdConfig?.find((x) => isValidInferenceCode(deviceState, inferenceCode, x.deviceState, x.L0));

    return !adConfig ? true : adConfig.isSpecialCode;
};

const runInferenceFlow = (deviceState, inferenceCode, isHolder, screenIndex = 0, isFwuFlow = false) => {
    setDiagnosticConfigData(deviceState, inferenceCode, isHolder, screenIndex, true);

    if (isFwuFlow) return appRouterService.forwardToUpdateCheckingPage();

    makeNextRedirect();
};

const setDiagnosticConfigData = (deviceState, inferenceCode, isHolder, screenIndex = 0, isActive) => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const {deviceConfig, holderConfig} = curDiagnosticResult;
    const adConfig = isHolder ? holderConfig : deviceConfig;
    let flow = adConfig?.flow;

    if (!flow) {
        if (!deviceState && !inferenceCode) {
            flow = advanceDiagnosticScreenSteps.SUCCESS_FLOW;
        } else {
            flow = getDeviceConfigByInferenceCode(inferenceCode, isHolder, deviceState)?.flow;
        }
    }

    const diagnosticResultObj = {
        ...curDiagnosticResult,
        [isHolder ? 'holderConfig' : 'deviceConfig']: {flow, curIndex: screenIndex, isActive},
    };

    dispatch(setDiagnosticFlowDataResult(diagnosticResultObj));
};

const isValidInferenceCode = (deviceState, inferenceCode, deviceStateInConfig, inferenceCodeInConfig) => {
    const isValid =
        inferenceCodeInConfig?.toLowerCase() === inferenceCode?.toLowerCase() &&
        deviceStateInConfig?.toLowerCase() === deviceState?.toLowerCase();

    if (isValid) return true;

    return deviceStateInConfig?.toLowerCase() === deviceState?.toLowerCase() && !inferenceCodeInConfig;
};

const getDeviceConfigByInferenceCode = (inferenceCode, isHolder, initDeviceState) => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const {deviceState: chargerDeviceState, holderDeviceState} = curDiagnosticResult;
    const deviceState = initDeviceState || (isHolder ? holderDeviceState : chargerDeviceState);

    const deviceAdConfig = getDeviceAdConfig(isHolder);
    const adConfig = deviceAdConfig?.find((x) => isValidInferenceCode(deviceState, inferenceCode, x.deviceState, x.L0));
    const flow = adConfig?.flow || getDeviceAdDefaultFlow(isHolder);

    if (!flow) {
        log.info(`Advance Diagnostic. Flow is not defined in config isHolder: ${isHolder}`);
        pairingService.disconnectAndShowCriticalGuide();
        return;
    }

    return {flow, curIndex: 0};
};

const selectAndRunInferenceFlow = (deviceInferenceCode, holderInferenceCode, chargerDeviceState, holderDeviceState) => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};

    amplitudeADTracking.trackADCompletedResolutionScreen({
        inferenceCode: deviceInferenceCode,
        isHolder: false,
        deviceState: chargerDeviceState,
    });
    amplitudeADTracking.trackADCompletedResolutionScreen({
        inferenceCode: holderInferenceCode,
        isHolder: true,
        deviceState: holderDeviceState,
    });

    const isDeviceError = deviceInferenceCode || chargerDeviceState;
    const isHolderError = holderInferenceCode || holderDeviceState;

    if (!isDeviceError || !isHolderError) {
        const isHolderFlow = !!isHolderError;
        const inferenceCode = isHolderFlow ? holderInferenceCode : deviceInferenceCode;
        const deviceState = isHolderFlow ? holderDeviceState : chargerDeviceState;

        return runInferenceFlow(deviceState, inferenceCode, isHolderFlow);
    }

    const deviceAdConfig = getDeviceConfigByInferenceCode(deviceInferenceCode, false, chargerDeviceState);
    const holderAdConfig = getDeviceConfigByInferenceCode(holderInferenceCode, true, holderDeviceState);

    const deviceConfig = {deviceConfig: {flow: deviceAdConfig.flow, curIndex: 0}};
    const holderConfig = {holderConfig: {flow: holderAdConfig.flow, curIndex: 0}};

    const diagnosticResultObj = {
        ...curDiagnosticResult,
        ...deviceConfig,
        ...holderConfig,
    };

    dispatch(setDiagnosticFlowDataResult(diagnosticResultObj));
    makeNextRedirect();
};

const getIsHolderFlow = () => {
    const diagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const {holderConfig, deviceInferenceCode, holderInferenceCode, holderDeviceState, deviceState} = diagnosticResult;
    const isActive = holderConfig?.isActive;

    return (!(deviceInferenceCode || deviceState) && (holderInferenceCode || holderDeviceState)) || isActive;
};

const setBothConfigActive = () => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const {deviceConfig, holderConfig} = curDiagnosticResult;

    const diagnosticResultObj = {
        ...curDiagnosticResult,
        deviceConfig: {flow: deviceConfig.flow, isActive: true},
        holderConfig: {flow: holderConfig.flow, isActive: true},
    };

    dispatch(setDiagnosticFlowDataResult(diagnosticResultObj));
};

const getADScreenData = (config, isHolder) => {
    const flow = config?.flow || getDeviceAdDefaultFlow(isHolder);
    let screenIndex;
    let screenType;

    const onError = () => {
        log.info(`MultiChoice screen. Advance Diagnostic. Flow is not defined in config isHolder: ${isHolder}`);
        pairingService.disconnectAndShowCriticalGuide();
    };

    if (!flow) {
        return onError();
    }
    const firstScreen = flow.screens.find((screen) => screen.index === 1 || screen.index === undefined);

    if (firstScreen?.name === advanceDiagnosticScreenTypes.FWU_CHECK_SCREEN) {
        const {isAnyFwuAvailable} = fwuPackageService.getFwuLoadedInfoStatic();

        if (isAnyFwuAvailable) {
            screenIndex = firstScreen.nextIndex;
        } else {
            screenIndex = firstScreen.noFwuNextIndex;
        }
        screenType = flow.screens.find((screen) => screen.index === screenIndex)?.name;
    } else {
        screenType = firstScreen?.name;
        screenIndex = firstScreen?.index || 1;
    }

    if (!screenType) {
        return onError();
    }

    return {screenType, screenIndex, isHolder};
};

const makeSingleRedirect = () => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const {deviceConfig, holderConfig, deviceInferenceCode, holderInferenceCode, holderDeviceState, deviceState} =
        curDiagnosticResult;
    const isMultipleResolution = (deviceInferenceCode || deviceState) && (holderInferenceCode || holderDeviceState);
    const {isAnyFwuAvailable} = fwuPackageService.getFwuLoadedInfoStatic();

    const isHolderFlow = getIsHolderFlow();
    const config = isHolderFlow ? holderConfig : deviceConfig;
    const {flow, curIndex} = config || {};

    if (!flow) {
        log.info(`Single screen. Advance Diagnostic. Flow is not defined in config isHolder: ${isHolderFlow}`);
        pairingService.disconnectAndShowCriticalGuide();
        return;
    }

    const screens = flow?.screens;
    const curScreen = curIndex ? screens.find((x) => x.index === curIndex) : null;
    const {nextIndex, noFwuNextIndex} = curScreen || {};

    if (curScreen && !nextIndex) {
        log.info(`Advance Diagnostic. Wrong nextIndex setup in config isHolder: ${isHolderFlow}`);
        pairingService.disconnectAndShowCriticalGuide();
        return;
    }

    let nextScreenIndex = nextIndex || 1;

    if (noFwuNextIndex) {
        nextScreenIndex = !isAnyFwuAvailable ? noFwuNextIndex : nextScreenIndex;
    }

    const nextScreenName =
        screens.find((x) => x.index === nextScreenIndex)?.name ||
        screens.find((x) => x.index === undefined && nextScreenIndex === 1)?.name;

    if (nextScreenName) {
        if (isMultipleResolution) {
            const isSingleConfigActive = getIsSingleConfigActive(deviceConfig, holderConfig);
            const isNextFinishScreen = nextScreenName === advanceDiagnosticScreenTypes.FINISH_SCREEN;
            const isDeviceConfigActive = deviceConfig?.isActive;
            const isHolderConfigActive = holderConfig?.isActive;

            if (isSingleConfigActive && isNextFinishScreen) {
                const partialScreenName = getPartialScreen(deviceConfig.flow, holderConfig.flow);

                const markAsResolved = (isHolder) => {
                    let adData;

                    if (isHolder) {
                        adData = {
                            ...curDiagnosticResult,
                            holderConfig: {...curDiagnosticResult.holderConfig, isResolved: true},
                            deviceConfig: {...curDiagnosticResult.deviceConfig, isActive: true},
                        };
                    } else {
                        adData = {
                            ...curDiagnosticResult,
                            deviceConfig: {...curDiagnosticResult.deviceConfig, isResolved: true},
                            holderConfig: {...curDiagnosticResult.holderConfig, isActive: true},
                        };
                    }
                    dispatch(setDiagnosticFlowDataResult(adData));
                };

                markAsResolved(isHolderConfigActive);

                return appRouterService.forwardToAdvancedDiagnosticScreen({screenName: partialScreenName});
            }

            if (isDeviceConfigActive && isHolderConfigActive) {
                const isDeviceConfigCheckFwu = getIsConfigCheckFwu(deviceConfig);
                const isHolderConfigCheckFwu = getIsConfigCheckFwu(holderConfig);
                const isTwoCheckFwuConfigs = isDeviceConfigCheckFwu && isHolderConfigCheckFwu;

                // if isTwoCheckFwuConfigs is true - this means that after multiple fwu screen we should always redirect to finish screen
                if (isNextFinishScreen || isTwoCheckFwuConfigs) {
                    return appRouterService.forwardToFinishScreenAD();
                }
            }
        }

        const diagnosticResultObj = {
            ...curDiagnosticResult,
            [isHolderFlow ? 'holderConfig' : 'deviceConfig']: {...config, curIndex: nextScreenIndex},
        };

        dispatch(setDiagnosticFlowDataResult(diagnosticResultObj));
        appRouterService.forwardToAdvancedDiagnosticScreen({screenName: nextScreenName});
    } else {
        log.info(`Advance Diagnostic. Wrong Index setup in config. isHolder: ${isHolderFlow}`);
        pairingService.disconnectAndShowCriticalGuide();
    }
};

const makeNextRedirect = (isFwuCheckScreen) => {
    const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const {deviceConfig, holderConfig, deviceInferenceCode, holderInferenceCode, holderDeviceState, deviceState} =
        curDiagnosticResult;
    const isMultipleResolution = (deviceInferenceCode || deviceState) && (holderInferenceCode || holderDeviceState);
    const {isAnyFwuAvailable} = fwuPackageService.getFwuLoadedInfoStatic();
    const isSameConfigs = getIsSameConfigs();
    const isAnyConfigActive = getIsAnyConfigActive(deviceConfig, holderConfig);

    if (isAnyConfigActive) {
        makeSingleRedirect();
        return;
    }

    if (isMultipleResolution) {
        const isTwoMarketFlows = getIsTwoMarketFlows(deviceConfig, holderConfig);
        const isTwoFWUMarketFlows = getIsTwoFWUMarketFlows(deviceConfig, holderConfig);
        const isNextMarketScreen = isFwuCheckScreen && isTwoFWUMarketFlows && !isAnyFwuAvailable;

        if (isTwoMarketFlows || isNextMarketScreen) {
            return appRouterService.forwardToMultipleIssuesScreenAD();
        }
    }

    if (isMultipleResolution && !isSameConfigs) {
        const isDeviceConfigCheckFwu = getIsConfigCheckFwu(deviceConfig);
        const isHolderConfigCheckFwu = getIsConfigCheckFwu(holderConfig);

        if (isFwuCheckScreen) {
            if (isDeviceConfigCheckFwu && isHolderConfigCheckFwu && isAnyFwuAvailable) {
                setBothConfigActive();
                return appRouterService.forwardToFwuScreenAD();
            }
            return appRouterService.forwardToMultipleIssuesScreenAD();
        }

        if (isDeviceConfigCheckFwu || isHolderConfigCheckFwu) {
            return appRouterService.forwardToFwuCheckScreenAD();
        } else {
            return appRouterService.forwardToMultipleIssuesScreenAD();
        }
    }

    makeSingleRedirect();
};

const isAfterFwuAdvanceDiagnosticRedirect = () => {
    const state = getState();
    const curDiagnosticResult = selectDiagnosticFlowDataResult(state) || {};
    const {isPending, deviceInferenceCode, holderInferenceCode, holderDeviceState, deviceState} = curDiagnosticResult;
    const isDeviceError = deviceInferenceCode || deviceState;
    const isHolderError = holderInferenceCode || holderDeviceState;

    return isDiagnosticFinished(curDiagnosticResult) && !isPending && (isDeviceError || isHolderError);
};

const getLocalizations = (advanceDiagnosticScreenType, defaultTitleKey, defaultDescriptionKey, isHolderRelated) => {
    const state = getState();
    const isMonoDevice = selectIsMonoDevice(state);
    const screenData = advanceDiagnosticLocalizationConfig.find((x) => x.screen === advanceDiagnosticScreenType);
    const {localizations = []} = screenData || {};
    const curDiagnosticResult = selectDiagnosticFlowDataResult(state) || {};
    const {deviceInferenceCode, holderInferenceCode, holderDeviceState, deviceState} = curDiagnosticResult;
    const isDeviceError = deviceInferenceCode || deviceState;
    const isHolderError = holderInferenceCode || holderDeviceState;

    let isHolder;
    let advanceDiagnosticLocalization;

    if (isHolderRelated === undefined) {
        if (!isDeviceError && !isHolderError) {
            advanceDiagnosticLocalization = localizations.find((x) => x.isSuccess === true);
        } else if (isMonoDevice) {
            advanceDiagnosticLocalization = localizations.find((x) => x.isMonoDevice === true);
        } else if (isDeviceError && isHolderError) {
            advanceDiagnosticLocalization = localizations.find((x) => x.isMultipleIssue === true);
        } else {
            isHolder = !!(!isDeviceError && isHolderError);
        }
    } else {
        isHolder = isHolderRelated;
    }

    if (!advanceDiagnosticLocalization) {
        advanceDiagnosticLocalization = localizations.find((x) => x.isHolder === isHolder);
    }

    const deviceName = selectProductName(state);
    const localizedStrings = getLocalizedStrings();
    const {titleKey, descriptionKey} = advanceDiagnosticLocalization || {};
    const title = localizedStrings.formatString(localizedStrings[titleKey || defaultTitleKey], deviceName);
    const description = localizedStrings[descriptionKey || defaultDescriptionKey];

    if (!title || !description) return {title: undefined, description: undefined};

    return {title, description};
};

const resetAdvanceDiagnosticFlow = () => {
    const state = getState();
    const curDiagnosticResult = selectDiagnosticFlowDataResult(state) || {};
    const diagnosticResultObj = {...curDiagnosticResult, isPending: true};

    dispatch(setDiagnosticFlowDataResult(diagnosticResultObj));
};

const setCurrentCodentify = (dataCollectionType, isHolder) => {
    if (dataCollectionType !== dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) return;

    if (!isHolder) return;

    const iotDevice = makeSelectIotDeviceMergedWithConsumerProduct()(getState());
    const codentify = iotDevice?.holder?.codentify;

    if (codentify) {
        const curDiagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
        const diagnosticResultObj = {...curDiagnosticResult, holderCodentifyStorage: codentify};

        dispatch(setDiagnosticFlowDataResult(diagnosticResultObj));
    }
};

const getIsNeedDiagnosticHolder = () => {
    const state = getState();
    const iotData = selectIotDeviceData(state);
    const isHolderConnected = selectIsHolderConnected(state);
    const isDeviceMono = selectIsMonoDevice(state);
    const isHolderInSBLMode = iotDeviceSettingsService.isHolderInSBLMode(iotData);
    const isNeedDiagnosticHolder = !isDeviceMono && isHolderConnected && !isHolderInSBLMode;

    return isNeedDiagnosticHolder;
};

const increaseFailedAttempt = (isHolder) => {
    const curFailedAttempts = selectFailedAttempts(getState());
    const {deviceFailedAttempts, holderFailedAttempts, isDeviceAlreadyFailed, isHolderAlreadyFailed} =
        curFailedAttempts || {};

    //alreadyFailed state exists to prevent chance that attempt will be increased several times in one go
    if ((isDeviceAlreadyFailed && !isHolder) || (isHolderAlreadyFailed && isHolder)) return;

    const attempts = isHolder ? holderFailedAttempts : deviceFailedAttempts;
    const failedAttemptsCount = attempts ? attempts + 1 : 1;

    const failedAttemptsIncreased = isHolder
        ? {holderFailedAttempts: failedAttemptsCount, isHolderAlreadyFailed: true}
        : {deviceFailedAttempts: failedAttemptsCount, isDeviceAlreadyFailed: true};

    dispatch(setFailedAttempts({...curFailedAttempts, ...failedAttemptsIncreased}));
};

const clearFailedAttempts = (isHolder) => {
    const curFailedAttempts = selectFailedAttempts(getState()) || {};
    const clearedAttempts = isHolder ? {holderFailedAttempts: 0} : {deviceFailedAttempts: 0};

    dispatch(setFailedAttempts({...curFailedAttempts, ...clearedAttempts}));
};

const clearAlreadyFailed = () => {
    const curFailedAttempts = selectFailedAttempts(getState()) || {};

    dispatch(setFailedAttempts({...curFailedAttempts, isDeviceAlreadyFailed: false, isHolderAlreadyFailed: false}));
};

const runFailedFlow = () => {
    const maxFailedAttempts = appConfig.getMaxFailedAttempts();

    //if the number of attempts is not configured - skip the logic of failed attempts
    if (!maxFailedAttempts) {
        return appRouterService.forwardToRunningFailAD();
    }

    const state = getState();
    const failedAttempts = selectFailedAttempts(state) || {};
    const {deviceFailedAttempts, holderFailedAttempts} = failedAttempts;
    const curDiagnosticResult = selectDiagnosticFlowDataResult(state) || {};
    const {deviceInferenceCode, holderInferenceCode, holderDeviceState, deviceState} = curDiagnosticResult;
    const isDeviceMaxFailedAttempts = deviceFailedAttempts > maxFailedAttempts;
    const isHolderMaxFailedAttempts = holderFailedAttempts > maxFailedAttempts;

    if (isDeviceMaxFailedAttempts || isHolderMaxFailedAttempts) {
        const deviceConfig = getDeviceConfigByInferenceCode(deviceInferenceCode, false, deviceState);
        const holderConfig = getDeviceConfigByInferenceCode(holderInferenceCode, true, holderDeviceState);

        const deviceDiagnosticData = isDeviceMaxFailedAttempts
            ? {
                  deviceConfig: {flow: advanceDiagnosticScreenSteps.MARKET_FLOW},
                  deviceInferenceCode: advanceDiagnosticFailedErrorCodes.FAILED_FLOW_DEVICE_INFERENCE_CODE,
                  deviceInferenceCode_L1: advanceDiagnosticFailedErrorCodes.FAILED_FLOW_DEVICE_INFERENCE_CODE,
                  isDeviceReceived: true,
                  isDeviceSuccess: true,
              }
            : {
                  deviceConfig,
              };

        const holderDiagnosticData = isHolderMaxFailedAttempts
            ? {
                  holderConfig: {flow: advanceDiagnosticScreenSteps.MARKET_FLOW},
                  holderInferenceCode: advanceDiagnosticFailedErrorCodes.FAILED_FLOW_HOLDER_INFERENCE_CODE,
                  holderInferenceCode_L1: advanceDiagnosticFailedErrorCodes.FAILED_FLOW_HOLDER_INFERENCE_CODE_L1,
                  isHolderReceived: true,
                  isHolderSuccess: true,
                  isHolder: true,
              }
            : {
                  holderConfig,
              };

        const diagnosticFlowDataResult = {...curDiagnosticResult, ...deviceDiagnosticData, ...holderDiagnosticData};

        amplitudeADTracking.trackADCompletedResolutionScreen({
            inferenceCode: diagnosticFlowDataResult?.deviceInferenceCode,
            isHolder: false,
            deviceState,
        });
        amplitudeADTracking.trackADCompletedResolutionScreen({
            inferenceCode: diagnosticFlowDataResult?.holderInferenceCode,
            isHolder: true,
            deviceState: holderDeviceState,
        });

        dispatch(setDiagnosticFlowDataResult(diagnosticFlowDataResult));
        dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC);

        return makeNextRedirect();
    }

    appRouterService.forwardToRunningFailAD();
};

const redirectToMarketUrl = (marketUrl) => {
    routerService.blankLinkOpen(marketUrl);
    analyticsService.pushDeviceDiagnosticContactUsEvent();
};

const getIsConfigCheckFwu = (config) =>
    config?.flow.screens?.some((item) => item?.name === advanceDiagnosticScreenTypes.FWU_CHECK_SCREEN);

const getIsSameConfigs = () => {
    const diagnosticResult = selectDiagnosticFlowDataResult(getState()) || {};
    const {holderConfig, deviceConfig} = diagnosticResult;

    if (!deviceConfig || !holderConfig) {
        return true;
    }

    return isSameFlow(holderConfig?.flow, deviceConfig?.flow);
};

const isSameFlow = (flow1, flow2) => JSON.stringify(flow1) === JSON.stringify(flow2);

const getIsMarketFlow = (flow) => isSameFlow(flow, advanceDiagnosticScreenSteps.MARKET_FLOW);

const getIsFWUMarketFlow = (flow) => isSameFlow(flow, advanceDiagnosticScreenSteps.FWU_CHECK_AND_MARKET_FLOW);

const getIsSuccessFlow = (flow) => isSameFlow(flow, advanceDiagnosticScreenSteps.SUCCESS_FLOW);

const getIsSuccessFlowForInferenceCode = (isHolder, deviceState, inferenceCode) => {
    const flow = getDeviceConfigByInferenceCode(inferenceCode, isHolder, deviceState)?.flow;

    return getIsSuccessFlow(flow);
};

const getIsTwoMarketFlows = (deviceConfig, holderConfig) => {
    const isDeviceMarketFlow = getIsMarketFlow(deviceConfig?.flow);
    const isHolderMarketFlow = getIsMarketFlow(holderConfig?.flow);

    return isDeviceMarketFlow && isHolderMarketFlow;
};

const getIsTwoFWUMarketFlows = (deviceConfig, holderConfig) => {
    const isDeviceFWUrMarketFlow = getIsFWUMarketFlow(deviceConfig?.flow);
    const isHoldeFWUrMarketFlow = getIsFWUMarketFlow(holderConfig?.flow);

    return isDeviceFWUrMarketFlow && isHoldeFWUrMarketFlow;
};

const getPartialScreen = (chargerFlow, holderFlow) => {
    const partialScreen = advanceDiagnosticPartialConfig.find(
        (x) => isSameFlow(x.chargerFlow, chargerFlow) && isSameFlow(x.holderFlow, holderFlow)
    )?.partialScreen;

    if (partialScreen) return partialScreen;

    return advanceDiagnosticPartialConfig.find((x) => x.chargerFlow === undefined && x.holderFlow === undefined)
        ?.partialScreen;
};

const getIsSingleConfigActive = (deviceConfig, holderConfig) => {
    const isDeviceConfigActive = deviceConfig?.isActive;
    const isHolderConfigActive = holderConfig?.isActive;

    return (isDeviceConfigActive && !isHolderConfigActive) || (!isDeviceConfigActive && isHolderConfigActive);
};

const getIsAnyConfigActive = (deviceConfig, holderConfig) => {
    const isDeviceConfigActive = deviceConfig?.isActive;
    const isHolderConfigActive = holderConfig?.isActive;

    return isDeviceConfigActive || isHolderConfigActive;
};

const clearDiagnosticState = () => {
    dispatch(clearAdvancedDiagnosticState());
    storageService.removeFinishAdvancedDiagnosticsDataFromLocalStorage();
};

const setInferenceCodeAndRedirect = (inferenceCode, isHolder, screenName, isQuestionnaireThirdStep) => {
    const currentInferenceCode = isHolder
        ? {
              holderInferenceCode: inferenceCode,
              holderInferenceCode_L1: inferenceCode,
          }
        : {
              deviceInferenceCode: inferenceCode,
              deviceInferenceCode_L1: inferenceCode,
          };

    dispatch(setDiagnosticFlowDataResult({isQuestionnaireThirdStep, ...currentInferenceCode}));
    appRouterService.forwardToAdvancedDiagnosticScreen({screenName});
};

const redirectToRunningScreen = async (isCheckHolderConnection = true) => {
    const isIccJourneyScreensEnabled = marketConfigService.isIccJourneyScreensEnabled();
    const isDeviceTroubleshootingEnabled = marketConfigService.isTNCEnabled()
        ? tncService.isAggregatedSettingEnabled(ENABLE_ERRORS_DISPLAY) && isIccJourneyScreensEnabled
        : true;

    if (!isDeviceTroubleshootingEnabled) {
        let isTncDataLoaded;

        await tncService.getTncData(() => (isTncDataLoaded = true), appErrorService.showGlobalError);

        if (!isTncDataLoaded) return;

        return appRouterService.forwardToConsentAD();
    }

    if (!isCheckHolderConnection) {
        return appRouterService.forwardToRunningAD();
    }

    const state = getState();
    const isHolderConnected = selectIsHolderConnected(state);
    const isMonoDevice = selectIsMonoDevice(state);

    if (!(isHolderConnected || isMonoDevice)) {
        return appRouterService.forwardToHolderDetectedAD();
    }

    appRouterService.forwardToRunningAD();
};

const getFinishData = () => {
    const {finishADChargerData, finishADHolderData} = storageService.getFinishAdvancedDiagnosticsDataFromLocalStorage();

    if (!finishADHolderData) {
        return {finishADChargerData};
    }

    if (!finishADChargerData) {
        return {finishADHolderData};
    }

    const {screenName: chargerScreenName, isPhase2: isChargerPhase2} = finishADChargerData;
    const {screenName: holderScreenName, isPhase2: isHolderPhase2} = finishADHolderData;

    //phase 1 is priority
    if (!isChargerPhase2 && isHolderPhase2) {
        return {finishADChargerData};
    }

    if (isChargerPhase2 && !isHolderPhase2) {
        return {finishADHolderData};
    }

    const marketScreen = advanceDiagnosticScreenTypes.MARKET_SCREEN;
    const multipleScreen = advanceDiagnosticScreenTypes.MULTIPLE_ISSUES_SCREEN;

    //merge market screens in phase 2 into multiple
    if (chargerScreenName === marketScreen && holderScreenName === marketScreen && isChargerPhase2 && isHolderPhase2) {
        finishADChargerData.screenName = multipleScreen;
        finishADChargerData.screenName = multipleScreen;
    }

    return {finishADChargerData, finishADHolderData};
};

const checkMaxSuccessfulAttempts = (isHolder, inferenceCode) => {
    const maxSuccessfulAttempts = appConfig.getMaxSuccessfulAttempts();

    //if the number of attempts is not configured - skip the logic of successful attempts
    if (!maxSuccessfulAttempts) {
        return false;
    }

    const {attempt, errorCode} = getDeviceADAttempts(isHolder) || {};

    //if the inferenceCode is different from what is in storage - reset the attempts
    if (inferenceCode !== errorCode) {
        removeDeviceADAttempt(isHolder);

        return false;
    }

    return attempt >= maxSuccessfulAttempts;
};

const getDeviceADAttempts = (isHolder) => {
    const serialNumber = makeSelectSerialNumber(isHolder)(getState());

    const storageData = storageService.getADAttemptsFromStorage();
    const deviceData = storageData?.find((x) => x.serialNumber === serialNumber);

    return deviceData;
};

const setDeviceADAttempts = (isHolder) => {
    const state = getState();
    const serialNumber = makeSelectSerialNumber(isHolder)(state);

    if (!serialNumber) return;

    const {deviceInferenceCode, holderInferenceCode, holderDeviceState, deviceState} =
        selectDiagnosticFlowDataResult(state) || {};
    const errorCode = isHolder ? holderInferenceCode || holderDeviceState : deviceInferenceCode || deviceState;

    if (!errorCode) return;

    const {attempt = 0} = getDeviceADAttempts(isHolder) || {};
    const storageData = storageService.getADAttemptsFromStorage();
    const filteredData = storageData.filter((x) => x.serialNumber !== serialNumber);
    const updatedData = {serialNumber, errorCode, attempt: attempt + 1};
    const logValue = JSON.stringify(updatedData);

    filteredData.push(updatedData);

    storageService.setADAttemptsToStorage(filteredData);

    log.info(
        `advanceDiagnosticService: Set value to local storage. Key: ${storageKeys.ADVANCED_DIAGNOSTICS_ATTEMPTS}, value: ${logValue}, deviceSerialNumber: ${serialNumber},`
    );
};

const removeDeviceADAttempt = (isHolder) => {
    const serialNumber = makeSelectSerialNumber(isHolder)(getState());

    if (!serialNumber) return;

    const storageData = storageService.getADAttemptsFromStorage();
    const filteredData = storageData.filter((x) => x.serialNumber !== serialNumber);

    storageService.setADAttemptsToStorage(filteredData);
};

export default {
    redirectToRunningScreen,
    setInferenceCompleted,
    getFinishData,
    setInferenceCodeAndRedirect,
    getIsSuccessFlowForInferenceCode,
    clearDiagnosticState,
    setDiagnosticConfigData,
    runInferenceFlow,
    checkDiagnosticResult,
    getLocalizations,
    isAfterFwuAdvanceDiagnosticRedirect,
    isDiagnosticFinished,
    makeNextRedirect,
    resetAdvanceDiagnosticFlow,
    selectAndRunInferenceFlow,
    setCurrentCodentify,
    setDiagnosticFailed,
    setDiagnosticPartResult,
    setDiagnosticReceived,
    getIsNeedDiagnosticHolder,
    increaseFailedAttempt,
    clearFailedAttempts,
    clearAlreadyFailed,
    runFailedFlow,
    isDiagnosticSuccess,
    getADScreenData,
    redirectToMarketUrl,
    getDeviceConfigByInferenceCode,
    checkMaxSuccessfulAttempts,
    setDeviceADAttempts,
    isSpecialFlow,
};
